import { useState, useEffect } from "react";
import { bindActionCreators, Dispatch } from "redux";
import { connect, RootStateOrAny } from "react-redux";
import { useHistory } from "react-router";

import { Card, Table, Typography } from "antd";
import _ from "lodash";

import { getMembersAndCaseDocuments, getCaseByUserId, getCaseLetterInfo } from "store/eligibility/caseDoc/actions";
import { downloadFileFromBlob } from "store/authorization/files/actions";

import { updateReadInfo } from "store/eligibility/pac/actions";
//eligibility info page related
import { SearchCriteria, search } from "store/eligibility/actions";

import { ErrorResponse } from "utils/types";
import { formatDate, handleDownload } from "utils/helpers";
import notifier from "utils/notifier";
import { UserRole } from "utils/enums";

const { Title } = Typography;

interface CaseDocumentsPageProps {
  updateReadInfo(passedUserId: string, passedPacUid: string, passedPacType: string): any;
  getMembersAndCaseDocuments(userId: string): any;
  getCaseByUserId(userId: string, pageIndex: number, pageSize: number): any;
  getCaseLetterInfo(caseNo: number, company: string): any;
  downloadFileFromBlob(companyId: string, application: string, filepath: string): any;
  isFetchingDocuments: boolean;
  isFetchingCaseLetterInfo: boolean;

  //eligibility info page related
  search(searchCriteria: SearchCriteria): any;
}

const CaseDocumentsPage = (props: CaseDocumentsPageProps) => {
  useEffect(() => {
    getCaseDocumentsByUserId();
  }, []);

  const history = useHistory();

  const [caseDocumentsDataSource, setCaseDocumentsDataSource] = useState<Object[]>([]);
  const [caseDocumentsExpandDataSource, setCaseDocumentsExpandDataSource] = useState<Object[]>([]);
  const [collapseState, setCollapseState] = useState<any>([]);

  const DEFAULT_PAGE_NUM = 1;
  const DEFAULT_PAGE_SIZE = 500;
  const [page, setPage] = useState(DEFAULT_PAGE_NUM);
  const [totalCaseDocuments, setTotalCaseDocuments] = useState(0);
  const [tableInfo, setTableInfo] = useState({} as any);

  const caseDocumentsColumns = [
    {
      title: "Member Name",
      key: "memberName",
      dataIndex: "memberName",
      render: (_: string, record: any) => (
        <>
          <div style={{display: 'inline'}}>{record.RmiLastNm + ', ' + record.RmiFirstNm} </div>
        </>
      )
    },
    {
      title: "Member ID",
      key: "RmiMembId",
      dataIndex: "RmiMembId",
      render: (_: string, record: any) => <a onClick={() => switchToEligibityDetailPage(record)}>{record.RmiMembId}</a>,
      sorter: (a: any, b: any) => a.RmiMembId.localeCompare(b.RmiMembId),
    },
    {
      title: "Latest Admit Date",
      key: "RmiAdmitDate",
      dataIndex: "RmiAdmitDate",
      render: (_: string, record: any) => formatDate(record.RmiAdmitDate, 'MM/DD/YYYY'),
      sorter: (a: any, b: any) => a.RmiAdmitDate.localeCompare(b.RmiAdmitDate),
    },
    {
      title: "Latest Discharge Date",
      key: "RmiDischargeDate",
      dataIndex: "RmiDischargeDate",
      render: (_: string, record: any) => formatDate(record.RmiDischargeDate, 'MM/DD/YYYY'),
      sorter: (a: any, b: any) =>  {
        a = a.RmiDischargeDate || '';
        b = b.RmiDischargeDate || '';
        return a.localeCompare(b);
      },
    },
    {
      title: "Auth No",
      key: "RmiAuthNo",
      dataIndex: "RmiAuthNo",
      sorter: (a: any, b: any) => a.RmiAuthNo.localeCompare(b.RmiAuthNo),
    },
  ];

  const caseDocumentsExpandColumns = [
    {
      title: "Document Name",
      key: "RmiFileName",
      dataIndex: "RmiFileName",
      render: (_: string, record: any) => <a onClick={() => downloadAttachment(record)}>{record.RmiFileName}</a>
    },
    {
      title: "Document Category",
      key: "RmiDocCategoryDesc",
      dataIndex: "RmiDocCategoryDesc"
    },
    {
      title: "Upload Date",
      key: "RmiUploadDate",
      dataIndex: "RmiUploadDate",
      render: (_: string, record: any) => formatDate(record.RmiUploadDate, 'MM/DD/YYYY')
    },
  ];

  const getCaseDocumentsByUserId = () => {
    return props.getCaseByUserId(sessionStorage.user_id, tableInfo.current || DEFAULT_PAGE_NUM, tableInfo.pageSize || DEFAULT_PAGE_SIZE)
      .then((response: any) => {
        const caseDocumentsResults: Object[] = [];
        for(const key in response) {
          caseDocumentsResults.push({...response[key], key: key});
        }

        setCaseDocumentsDataSource(caseDocumentsResults);
        setTotalCaseDocuments(caseDocumentsResults.length)
        // onShowTable(table); //recalls the API to get the updated read by and read date
      })
      .catch((err: ErrorResponse) =>
        notifier.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.')
      );
  }

  const getCaseLetterInfo = (caseNo: number, companyId: string) => {
    return props.getCaseLetterInfo(caseNo, companyId)
      .then((response: any) => {
        const caseDocumentsResults: Object[] = [];
        for(const key in response) {
          caseDocumentsResults.push({...response[key], key: key});
        }

        setCaseDocumentsExpandDataSource(response);
        // onShowTable(table); //recalls the API to get the updated read by and read date
      })
      .catch((err: ErrorResponse) =>
        notifier.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.')
      );
  }

  //Update Read Info API after you click on download member document link
  const updateReadInfo = (document: any, pacType: string) => {
    return props.updateReadInfo(sessionStorage.user_id, document.RmiUid, pacType)
      .then(() => {
        // onShowTable(table); //recalls the API to get the updated read by and read date
      })
      .catch((err: ErrorResponse) =>
        notifier.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.')
      );
  }

  //Download Documents API when you click on the links on the table
  //document too long to define
  const downloadAttachment = (documentFile: any) => {
    return props.downloadFileFromBlob(sessionStorage.getItem('ipaGroup')!, 'provider-portal', documentFile.RmiFilePath)
      .then((response: any) => {
        handleDownload(response, documentFile.RmiFileName);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.')
      });
  }

  const switchToEligibityDetailPage = (memberInfo: any) => {
    //According to legacy code
    const provider = (sessionStorage.user_role === UserRole.CASE || sessionStorage.user_role === UserRole.IPA) ? "OTHER" : "PCP";
    let formData = {
      "DateOfBirth": memberInfo.RmiBirth,
      "MemberId": memberInfo.RmiMembId,
      "Provider": provider,
      "UserId": sessionStorage.user_id
    }

    return props.search(formData)
      .then((response: any) => {
        if(response.length > 0) {
          history.push({
            pathname: '/eligibility/member-detail',
            state: {
              eligibilityMemberInfo: response[0]
            }
          });

        } else {
          notifier.error('No access. Please use eligibility to search for this member.');
        }
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  }

  const handleRowExpand = (expanded: any, record: any) => {
    //if it's a different row key, then call the API and expand the accordion
    const keys = [];

    if (expanded) {
      getCaseLetterInfo(record.RmiCaseNo, sessionStorage.getItem('ipaGroup')!);
      keys.push(record.key);
    }
    setCollapseState(keys);
  }

  return (
    <div className="documents-page">
      <div className="header-title">
        <Title level={4} className="ml-3">Case Documents</Title>
      </div>

      <div className="search-section">
        <Table
          columns={caseDocumentsColumns}
          dataSource={caseDocumentsDataSource}
          pagination={{
            current: page,
            position: ['bottomCenter'],
            showSizeChanger: true,
            defaultPageSize: 20,
            total: totalCaseDocuments,
            hideOnSinglePage: true,
            onChange: (num: number) => {
              setPage(num);
            }
          }}
          onChange={(state) => {
            setTableInfo({...state});
          }}
          expandable={{
            expandedRowRender: record =>
              (<>
                <Card style={{ width: '100%' }}>
                  <Table
                    columns={caseDocumentsExpandColumns}
                    dataSource={caseDocumentsExpandDataSource}
                    pagination={false}
                    loading={props.isFetchingCaseLetterInfo}
                  />
                </Card>
              </>),
          }}
          onExpand={(expanded, record) => handleRowExpand(expanded, record)}
          expandedRowKeys={collapseState}
          loading={props.isFetchingDocuments}
        />
      </div>
    </div>
  );
};

const mapStateToProps = (store: RootStateOrAny) => ({
  isFetchingDocuments: store.rootReducer.eligibilityCaseDoc.isFetchingDocuments, // Case Documents
  isFetchingCaseLetterInfo: store.rootReducer.eligibilityCaseDoc.isFetchingCaseLetterInfo, // Fetch Case Letter info from Blob
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators(
  {
    getMembersAndCaseDocuments,
    downloadFileFromBlob,
    updateReadInfo,
    getCaseByUserId,
    getCaseLetterInfo,

    //eligibility info page related
    search,
  },
  dispatch
);

export default connect(mapStateToProps, mapDispatchToProps)(CaseDocumentsPage);
